import React, { Suspense, useContext } from 'react'
import { Layout, Spin } from 'antd'
import { Redirect } from 'react-router'
import { routes } from '../../config/routes'
import AppHeader from '../Header/Header'
import Sidebar from '../Sidebar/Sidebar'
import { useCallback } from 'react'
import { getUserCookie } from '../../utils/helperFunctions/cookieToken'
import { AuthContext } from '../../store/auth'
import { UserRoles } from '../../utils/constants/constant'

const { Content } = Layout

function ProtectedRoute({ route, componentKey }) {
  const currentUserData = getUserCookie()
  const { userRole, roleLoading } = useContext(AuthContext)
  // Auth token is now an HttpOnly cookie managed by the backend and is not
  // accessible from JS. tokenExpiry is only non-sensitive session metadata --
  // it lives in a plain (non-HttpOnly) cookie, so it is JS-readable/writable
  // and must never be trusted alone as proof of authentication. userRole is
  // only ever set from a backend response (login's API result, or
  // fetchAndSetRole's getUserProfile call in authHook.js on refresh), so
  // requiring it to be one of the known roles is what actually reflects a
  // backend-confirmed session; an unresolved/unknown/malformed role fails
  // closed to "not authenticated".
  const hasUnexpiredCookie =
    !!currentUserData?.tokenExpiry &&
    new Date(currentUserData.tokenExpiry).getTime() > Date.now()
  const hasConfirmedRole = Object.values(UserRoles).includes(userRole)
  const isAuthenticated = hasUnexpiredCookie && hasConfirmedRole
  // While the backend role/session check is still in flight for a secure
  // route, don't render the route (would rely on a not-yet-confirmed role)
  // and don't redirect to login either (would be premature) -- show the
  // same loading pattern already used below for lazy-loaded route chunks.
  const isResolvingAuth = route.secure && roleLoading

  const renderLayout = useCallback(
    (val) => {
      if (val) {
        return isAuthenticated ? (
          <route.component key={componentKey} />
        ) : (
          <Redirect to={routes?.loginpage?.path} />
        )
      } else {
        // Ensure non-secure routes like resetpassword are handled correctly
        if (route.path === '/' && isAuthenticated) {
          const defaultRoutePath =
            userRole === UserRoles.PM
              ? routes?.projectdelivery?.path
              : routes?.projectlist?.path
          return <Redirect to={defaultRoutePath} />
        }
        return <route.component key={componentKey} />
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, userRole, componentKey]
  )

  if (isResolvingAuth) {
    return (
      <div className="fallback-spin">
        <Spin />
      </div>
    )
  }

  return (
    <Layout>
      {/* Header/Sidebar are chrome for an authenticated session -- gating them
          on isAuthenticated (not just the static route.noSidebar/noHeader
          config) ensures they disappear the instant the session is
          invalidated (e.g. exact-time expiry while idle), instead of staying
          mounted on whatever secure route the user was last on. */}
      {!route.noHeader && isAuthenticated && <AppHeader />}
      <div
        className={!route.noHeader && isAuthenticated ? 'page-wrapper' : 'no-header-page-wrapper'}
      >
        <Layout>
          {!route.noSidebar && isAuthenticated && <Sidebar />}
          <Suspense
            className="suspense"
            fallback={
              <div className="fallback-spin">
                <Spin />
              </div>
            }
          >
            <Layout>
              <div className="page-content">
                <Content className="site-layout-background">
                  {renderLayout(route.secure)}
                </Content>
              </div>
            </Layout>
          </Suspense>
        </Layout>
      </div>
    </Layout>
  )
}

export default ProtectedRoute
